/* eslint-disable @typescript-eslint/no-explicit-any */
// @ts-nocheck - This file is a work in progress and is not fully implemented
import { Delete, CloudUpload, InfoOutlined } from '@mui/icons-material';
import {
  Button,
  Card,
  CardMedia,
  IconButton,
  Box,
  Tooltip,
} from '@mui/material';
import React, { useEffect, useState, useRef } from 'react';
import {
  Stage,
  Layer,
  Image as KonvaImage,
  Transformer,
  Group,
  Shape,
} from 'react-konva';
import { TProductCustomizer } from './ProductCustomizer.types';
import './ProductCustomizer.styled.scss';
import phoneCaseBlackTemplate from './templates/phoneCases/iphone14ProMax/black.png';

const MAX_IMAGES = 5;
const OUT_OF_BOUNDS_OPACITY = 0.15;

export const ProductCustomizer: TProductCustomizer = ({
  template: { clip: clipFunc, template: phoneCasePath },
}) => {
  const DEFAULT_CANVAS_SIZE = 600;
  const TEMPLATE_SIZE = 300;

  const stageRef = useRef<Stage>(null);
  const canvasContainerRef = useRef<HTMLDivElement>(null);
  const transformerRef = useRef<any>(null);
  const outOfBoundsLayerRef = useRef<any>(null);
  const imageRefs = useRef<{ [key: string]: any }>({});
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [canvasSize, setCanvasSize] = useState({
    width: DEFAULT_CANVAS_SIZE,
    height: DEFAULT_CANVAS_SIZE,
  });
  const TEMPLATE_X = canvasSize.width / 2 - TEMPLATE_SIZE / 2;
  const TEMPLATE_Y = canvasSize.height / 2 - TEMPLATE_SIZE / 2;

  const [images, setImages] = useState<
    {
      id: string;
      src: string;
      x: number;
      y: number;
      width: number;
      height: number;
      rotation?: number;
      locked: boolean;
      imgObj?: HTMLImageElement;
    }[]
  >([]);

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [templateImage, setTemplateImage] = useState<HTMLImageElement | null>(
    null
  );

  useEffect(() => {
    const image = new window.Image();
    image.src = phoneCaseBlackTemplate;
    image.onload = () => setTemplateImage(image);
  }, []);

  useEffect(() => {
    const measureCanvas = () => {
      if (!canvasContainerRef.current) return;
      const { clientWidth, clientHeight } = canvasContainerRef.current;
      const style = window.getComputedStyle(canvasContainerRef.current);
      const horizontalPadding =
        parseFloat(style.paddingLeft || '0') +
        parseFloat(style.paddingRight || '0');
      const verticalPadding =
        parseFloat(style.paddingTop || '0') +
        parseFloat(style.paddingBottom || '0');

      const nextWidth = Math.max(
        Math.floor(clientWidth - horizontalPadding),
        1
      );
      const nextHeight = Math.max(
        Math.floor(clientHeight - verticalPadding),
        1
      );

      setCanvasSize((prev) =>
        prev.width === nextWidth && prev.height === nextHeight
          ? prev
          : {
              width: nextWidth,
              height: nextHeight,
            }
      );
    };

    measureCanvas();
    window.addEventListener('resize', measureCanvas);
    const observer = new ResizeObserver(measureCanvas);
    if (canvasContainerRef.current) {
      observer.observe(canvasContainerRef.current);
    }

    return () => {
      window.removeEventListener('resize', measureCanvas);
      observer.disconnect();
    };
  }, []);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Delete') {
        removeImage(selectedId);
      }
      if (e.key === 'Escape') {
        setSelectedId(null);
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [selectedId]);
  // Load image objects once

  useEffect(() => {
    images.forEach((img, index) => {
      if (!img.imgObj && img.src) {
        const newImg = new window.Image();
        newImg.src = img.src;
        newImg.crossOrigin = 'anonymous';
        newImg.onload = () => {
          setImages((prev) => {
            const copy = [...prev];
            if (copy[index]) {
              copy[index].imgObj = newImg;
            }
            return copy;
          });
        };
        selectImage(img.id);
        newImg.onerror = () => {
          console.error('Failed to load image:', img.src);
        };
      }
    });
  }, [images]);

  const selectImage = React.useCallback(
    (id: string) => {
      const img = images.find((i) => i.id === id);

      if (!img) return;
      setSelectedId(id);
    },
    [images]
  );

  const updateImage = (id: string, attrs: any) => {
    setImages((imgs) =>
      imgs.map((i) => (i.id === id ? { ...i, ...attrs } : i))
    );
  };

  const renderInteractiveImage = (img: {
    id: string;
    x: number;
    y: number;
    width: number;
    height: number;
    rotation?: number;
    imgObj?: HTMLImageElement;
  }) => (
    <KonvaImage
      key={img.id}
      // eslint-disable-next-line no-return-assign
      ref={(node) => (imageRefs.current[img.id] = node)}
      image={img.imgObj || null}
      x={img.x}
      y={img.y}
      width={img.width}
      height={img.height}
      rotation={img.rotation || 0}
      draggable
      onMouseDown={() => selectImage(img.id)}
      onClick={() => selectImage(img.id)}
      onTap={() => selectImage(img.id)}
      onDragStart={() => selectImage(img.id)}
      onDragMove={(e) =>
        updateImage(img.id, {
          x: e.target.x(),
          y: e.target.y(),
        })
      }
      onDragEnd={(e) =>
        updateImage(img.id, {
          x: e.target.x(),
          y: e.target.y(),
        })
      }
      onTransformEnd={() => {
        const node = imageRefs.current[img.id];
        const scaleX = node.scaleX();
        const scaleY = node.scaleY();
        updateImage(img.id, {
          x: node.x(),
          y: node.y(),
          width: node.width() * scaleX,
          height: node.height() * scaleY,
          rotation: node.rotation(),
        });
        node.scaleX(1);
        node.scaleY(1);
      }}
    />
  );

  const renderClippedOverlayImage = (img: {
    id: string;
    x: number;
    y: number;
    width: number;
    height: number;
    rotation?: number;
    imgObj?: HTMLImageElement;
  }) => (
    <KonvaImage
      key={`${img.id}_clip`}
      image={img.imgObj || null}
      x={img.x}
      y={img.y}
      width={img.width}
      height={img.height}
      rotation={img.rotation || 0}
      listening={false}
    />
  );

  const removeImage = (id: string) => {
    setImages((prev) => prev.filter((img) => img.id !== id));
    if (selectedId === id) {
      setSelectedId(null);
    }
    delete imageRefs.current[id];
  };

  const handleUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;

    const remainingSlots = MAX_IMAGES - images.length;
    const filesToProcess = files.slice(0, remainingSlots);

    if (filesToProcess.length < files.length) {
      alert(
        `You can only upload up to ${MAX_IMAGES} images. Only the first ${filesToProcess.length} will be added.`
      );
    }

    filesToProcess.forEach((file) => {
      const reader = new FileReader();
      reader.onload = () => {
        const newId = 'img_' + Date.now() + '_' + Math.random();
        setImages((prev) => [
          ...prev,
          {
            id: newId,
            src: reader.result as string,
            x: TEMPLATE_X + 40,
            y: TEMPLATE_Y + 40,
            width: 200,
            height: 200,
            rotation: 0,
            locked: false,
          },
        ]);
      };

      reader.readAsDataURL(file);
    });

    // Reset input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const exportImage = () => {
    if (!stageRef.current) return;
    const outOfBoundsLayer = outOfBoundsLayerRef.current;
    const prevVisibility = outOfBoundsLayer?.visible?.();

    try {
      if (outOfBoundsLayer) {
        outOfBoundsLayer.visible(false);
        outOfBoundsLayer.getLayer()?.batchDraw();
      }

      const sourceCanvas = stageRef.current.toCanvas({ pixelRatio: 2 });
      const context = sourceCanvas.getContext('2d');
      if (!context) return;

      const { width, height } = sourceCanvas;
      const { data } = context.getImageData(0, 0, width, height);

      let minX = width;
      let minY = height;
      let maxX = -1;
      let maxY = -1;

      for (let y = 0; y < height; y += 1) {
        for (let x = 0; x < width; x += 1) {
          const alpha = data[(y * width + x) * 4 + 3];
          if (alpha > 0) {
            minX = Math.min(minX, x);
            minY = Math.min(minY, y);
            maxX = Math.max(maxX, x);
            maxY = Math.max(maxY, y);
          }
        }
      }

      // Fallback to full export when no visible pixels found.
      if (maxX < minX || maxY < minY) {
        const fallbackUri = sourceCanvas.toDataURL('image/png');
        const fallbackLink = document.createElement('a');
        fallbackLink.href = fallbackUri;
        fallbackLink.download = 'product-customizer.png';
        fallbackLink.click();
        return;
      }

      const trimmedWidth = maxX - minX + 1;
      const trimmedHeight = maxY - minY + 1;
      const trimmedCanvas = document.createElement('canvas');
      trimmedCanvas.width = trimmedWidth;
      trimmedCanvas.height = trimmedHeight;
      const trimmedContext = trimmedCanvas.getContext('2d');
      if (!trimmedContext) return;

      trimmedContext.drawImage(
        sourceCanvas,
        minX,
        minY,
        trimmedWidth,
        trimmedHeight,
        0,
        0,
        trimmedWidth,
        trimmedHeight
      );

      const uri = trimmedCanvas.toDataURL('image/png');
      const link = document.createElement('a');
      link.href = uri;
      link.download = 'product-customizer.png';
      link.click();
    } finally {
      if (outOfBoundsLayer && typeof prevVisibility === 'boolean') {
        outOfBoundsLayer.visible(prevVisibility);
        outOfBoundsLayer.getLayer()?.batchDraw();
      }
    }
  };

  // Update transformer when selectedId changes
  useEffect(() => {
    if (selectedId && transformerRef.current && imageRefs.current[selectedId]) {
      transformerRef.current.nodes([imageRefs.current[selectedId]]);
      transformerRef.current.getLayer().batchDraw();
    }
  }, [selectedId]);

  return (
    <div className="product-customizer">
      <Card className="product-customizer__panel">
        <div className="product-customizer__toolbar">
          <div className="product-customizer__toolbar__left">
            {['Cutout', 'Background', 'Effects', 'Adjust', 'Design'].map(
              (item) => (
                <button
                  key={item}
                  type="button"
                  className="product-customizer__toolbar__placeholder-button"
                >
                  <span className="product-customizer__toolbar__placeholder-dot" />
                  {item}
                </button>
              )
            )}
          </div>
          <div className="product-customizer__toolbar__right">
            <button
              type="button"
              className="product-customizer__toolbar__icon-button"
              aria-label="Undo"
            >
              ↶
            </button>
            <button
              type="button"
              className="product-customizer__toolbar__icon-button"
              aria-label="Redo"
            >
              ↷
            </button>
            <Button
              variant="contained"
              className="product-customizer__toolbar__add-to-cart-button"
            >
              Add to cart
            </Button>
          </div>
        </div>

        <Box className="product-customizer__instructions-tooltip">
          <Tooltip
            placement="left-start"
            arrow
            title={
              <Box sx={{ padding: 0.5 }}>
                <ul
                  style={{
                    margin: 0,
                    paddingLeft: '16px',
                  }}
                >
                  <li>Upload up to {MAX_IMAGES} images</li>
                  <li>Click an image on the canvas to select it</li>
                  <li>Drag, resize, and rotate as needed</li>
                  <li>Out-of-bounds area is shown semi-transparent</li>
                  <li>Press Delete to remove selected image</li>
                  <li>Export your design when finished</li>
                </ul>
              </Box>
            }
          >
            <Button
              variant="outlined"
              size="small"
              startIcon={<InfoOutlined fontSize="small" />}
            >
              Instructions
            </Button>
          </Tooltip>
        </Box>

        <div className="product-customizer__canvas-section">
          <div
            ref={canvasContainerRef}
            className="product-customizer__canvas-section__canvas-container"
          >
            <Stage
              ref={stageRef}
              width={canvasSize.width}
              height={canvasSize.height}
              onMouseDown={(e) => {
                if (e.target === e.target.getStage()) setSelectedId(null);
              }}
              onMouseLeave={() => setSelectedId(null)}
              onTouchStart={(e) => {
                if (e.target === e.target.getStage()) setSelectedId(null);
              }}
            >
              <Layer>
                {/* Draw phone case outline with camera cutouts */}
                {/* <Shape
                  sceneFunc={(ctx) =>
                    phoneCasePath(ctx, canvasSize.width, canvasSize.height)
                  }
                /> */}
                <KonvaImage
                  image={templateImage}
                  x={canvasSize.width / 2 - (80.8 * 3) / 2}
                  y={canvasSize.height / 2 - (163.4 * 3) / 2}
                  width={80.8 * 3}
                  height={163.4 * 3}
                />

                {/* Full image layer (outside case appears semi-transparent) */}
                <Group
                  ref={outOfBoundsLayerRef}
                  opacity={OUT_OF_BOUNDS_OPACITY}
                >
                  {images.map((img) => renderInteractiveImage(img))}
                </Group>

                {/* Clipped image layer (inside case is fully visible) */}
                <Group
                  clipFunc={(ctx) =>
                    clipFunc(ctx, canvasSize.width, canvasSize.height)
                  }
                  listening={false}
                >
                  {images.map((img) => renderClippedOverlayImage(img))}
                </Group>

                {/* Transformer */}
                {selectedId && imageRefs.current[selectedId] && (
                  <Transformer
                    ref={transformerRef}
                    rotateEnabled
                    keepRatio={false}
                    enabledAnchors={[
                      'top-left',
                      'top-right',
                      'bottom-left',
                      'bottom-right',
                    ]}
                    borderStroke="orange"
                    borderStrokeWidth={2}
                    // add anchors
                    anchorFill="orange"
                    anchorStrokeWidth={1}
                    anchorSize={15}
                    // make all anchors look like circles
                    anchorCornerRadius={20}
                  />
                )}
              </Layer>
            </Stage>
          </div>
        </div>

        <div className="product-customizer__upload-strip">
          <Button
            variant="outlined"
            component="label"
            className="product-customizer__upload-strip__upload-button"
            disabled={images.length >= MAX_IMAGES}
          >
            <CloudUpload />
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              multiple
              hidden
              onChange={handleUpload}
              disabled={images.length >= MAX_IMAGES}
            />
          </Button>

          <div className="product-customizer__upload-strip__images">
            {images.map((img) => (
              <Card
                key={img.id}
                className="product-customizer__upload-strip__image-card"
                onClick={() => selectImage(img.id)}
                sx={{
                  position: 'relative',
                  cursor: 'pointer',
                  border:
                    selectedId === img.id
                      ? '2px solid rgba(255, 111, 0, 0.95)'
                      : '2px solid transparent',
                }}
              >
                <CardMedia
                  component="img"
                  image={img.src}
                  sx={{ objectFit: 'cover' }}
                />
                <IconButton
                  size="small"
                  onClick={() => removeImage(img.id)}
                  sx={{
                    position: 'absolute',
                    top: 4,
                    right: 4,
                    background: 'rgba(0,0,0,0.6)',
                    color: '#fff',
                    '&:hover': {
                      background: 'rgba(211, 47, 47, 0.8)',
                    },
                  }}
                >
                  <Delete fontSize="small" />
                </IconButton>
              </Card>
            ))}
          </div>
          <Button
            variant="contained"
            onClick={exportImage}
            className="product-customizer__upload-strip__export-button"
          >
            Export image
          </Button>
        </div>
      </Card>
    </div>
  );
};
